import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import apiClient from "../utils/apiClient";
import { toast } from "react-toastify";
import {ArrowPathIcon, ShieldCheckIcon, ExclamationTriangleIcon } from "@heroicons/react/24/outline";
import { useAuth } from "../contexts/authContext";

import { Capacitor } from '@capacitor/core';
import { Purchases as PurchasesNative, LOG_LEVEL as LOG_LEVEL_NATIVE } from '@revenuecat/purchases-capacitor';
import { Purchases as PurchasesWeb } from '@revenuecat/purchases-js';

export default function AccountPage() {
  const [user, setUser] = useState(null);
  const [email, setEmail] = useState("");
  const navigate = useNavigate();
  const userId = localStorage.getItem("userId"); // Adjust if your user ID is stored differently
  const { logout } = useAuth();  // get the login function from context

    // Define state variables to store the subscription information
  // Initialize with null or default values
  const [subscriptionStore, setSubscriptionStore] = useState(null);
  const [subscriptionStoreMsg, setSubscriptionStoreMsg] = useState(null);
  const [isProActive, setIsProActive] = useState(false);
  const [isLoadingSubscription, setIsLoadingSubscription] = useState(true);
  const [subscriptionError, setSubscriptionError] = useState(null);

  useEffect(() => {
    const fetchSubscriptionInfo = async () => {
      try {
        if (Capacitor.isNativePlatform && Capacitor.isNativePlatform()) {
          // Native platform logic
          //const customerInfo = await PurchasesNative.getCustomerInfo();
          const { customerInfo } = await PurchasesNative.getCustomerInfo({ fetchPolicy: 'FETCH_CURRENT' });
          console.log('➡️ Native customerInfo.entitlements.active =', customerInfo.entitlements?.active);

          const isActive = !!customerInfo.entitlements?.active?.['sous_premium'];
          setIsProActive(isActive);

          toast.info(customerInfo.entitlements);

          let store;
          let msg;
          const premiumEntitlement = customerInfo.entitlements?.active?.['sous_premium'];

          if (premiumEntitlement) {
            console.log("ENTITLEMENT FOUND");
            switch (premiumEntitlement.store) {
              case "PLAY_STORE": store = "Play Store"; msg = "Please manage your subscription from the Play Store"; break;
              case "APP_STORE": store = "App Store"; msg = "Please manage your subscription from the App Store"; break;
              case "STRIPE": store = "Stripe"; msg = "Please check your receipt for the link to manage your subscription"; break; // RevenueCat web often uses Stripe
              default: store = "Unknown Web Store"; msg = "Please check your receipt for the link to manage your subscription";
            }
          }
          setSubscriptionStore(store);
          setSubscriptionStoreMsg(msg);

        } else {
          // Web platform logic
          const customerInfo = await PurchasesWeb.getSharedInstance().getCustomerInfo();
          console.log('➡️ Web customerInfo.entitlements.active =', customerInfo.entitlements?.active);

          let store;
          let msg;
          // Safely access properties using optional chaining
          const premiumEntitlement = customerInfo.entitlements?.active?.['sous_premium'];

          if (premiumEntitlement) {
            switch (premiumEntitlement.store) {
              case "play_store": store = "Play Store"; msg = "Please manage your subscription from the Play Store"; break;
              case "app_store": store = "App Store"; msg = "Please manage your subscription from the App Store"; break;
              case "stripe": store = "Stripe"; msg = "Please check your receipt for the link to manage your subscription"; break; // RevenueCat web often uses Stripe
              default: store = "Unknown Web Store"; msg = "Please check your receipt for the link to manage your subscription";
            }
            setIsProActive(premiumEntitlement.isActive);
          } else {
            store = "No Active Premium Subscription (Web)";
            setIsProActive(false);
          }
          setSubscriptionStore(store);
          setSubscriptionStoreMsg(msg);
        }
      } catch (e) {
        console.error('Failed to fetch customer info:', e);
        setSubscriptionError(e.message || 'Error fetching subscription info.');
        setIsProActive(false); // Assume not active on error
        setSubscriptionStore('Error');
      } finally {
        setIsLoadingSubscription(false); // Set loading to false once fetch is complete
      }
    };

    fetchSubscriptionInfo();
  }, []); // Empty dependency array ensures this runs once on component mount

  const handleLogout = () => {
    localStorage.clear();
    logout();
    navigate("/login");
  };

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const res = await apiClient.get(`/users`);
        setUser(res.data.user);
        setEmail(res.data.user.email); // prefill email field
      } catch (err) {
        toast.error("Failed to fetch user data.");
      }
    };
    fetchUser();
  }, [userId]);

  const handlePasswordReset = async (e) => {
    e.preventDefault();
    try {
      await apiClient.post("/auth/forgot-password", { email });
      toast.success("Reset link sent! Check your email.");
    } catch (err) {
      toast.error("Could not send reset email.");
    }
  };

  const handleDeleteAccount = async () => {
    try {
      await apiClient.delete(`/users/${userId}`);
      toast.success("Account deleted. Logging out...");
      setTimeout(() => {
        handleLogout();
      }, 5000);
    } catch (err) {
      toast.error("Error deleting account.");
    }
  };

  if (!user) {
    return <div className="p-4">Loading account info...</div>;
  }

  if (isLoadingSubscription) {
    return <div className="text-center py-8">Loading subscription status...</div>;
  }

  if (subscriptionError) {
    return <div className="text-center py-8 text-red-600">Error: {subscriptionError}</div>;
  }

  return (
    <div className="max-w-md mx-auto mt-10 space-y-6 px-4">
      <h1 className="text-2xl font-semibold">Account settings</h1>
      
      <div className="p-4 bg-gray-100 rounded-lg ">
        <h2 className="flex gap-3 font-medium mb-4">
          <ArrowPathIcon className="h-6 w-6 text-sage" />
          Manage Subscription
        </h2>
          You are on the <b>{isProActive ? 'pro tier' : 'free tier'}</b>
          {isProActive ? 
            <p className="text-sm">{subscriptionStoreMsg}</p>
          : 
          <button 
            className="w-full rounded-3xl bg-sage text-white my-2 px-4 py-2 hover:bg-sagedark "
            onClick={() => navigate("/subscribe")}
          >
            Subscribe for unlimited access
          </button>
          }

      </div>
      
      <div className="p-4 bg-gray-100 rounded-lg ">
        <h2 className="flex gap-3 font-medium mb-4">
        <ShieldCheckIcon className="h-6 w-6 text-sage" />
          Reset Password
        </h2>
        <form onSubmit={handlePasswordReset}>
          <label className="block mb-2 text-sm font-medium text-gray-700">Email address</label>
          <input
            type="email"
            className="w-full p-2 border border-gray-300 rounded-md mb-4"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
          <button
            type="submit"
            className="w-full rounded-3xl bg-sage text-white px-4 py-2 hover:bg-sagedark transition"
          >
            Send Password Reset Email
          </button>
        </form>
      </div>
      <div className="p-4 bg-gray-100 rounded-lg">
        <h2 className="flex gap-3 font-medium mb-4 text-red-700">
          <ExclamationTriangleIcon className="h-6 w-6"/>
          Danger Zone
        </h2>
        <p className="mb-4">
          Deleting your account is permanent, this action cannot be undone. 
        </p>
        <button
            onClick={handleDeleteAccount}
            className="w-full rounded-3xl bg-red-600 text-white px-4 py-2 hover:bg-red-700 transition"
          >
            Delete My Account
          </button>
      </div>
    </div>
  );
};
